import {inject, Injectable} from "@angular/core";
import {SessionModel} from "../session/session.model";
import {ConfigurationModel, ParamConfig} from "../configuration/configuration.model";
import {DisplayableOrderItem, OrderItem, OrderType} from '../order/order';
import {OrderModel} from '../order/order.model';
import {UnavailabilityReason} from '../order/order-initiation.service';
import {OrderingContextModel} from '../ordering-context/ordering-context.model';
import {PaymentType, WalletProvider} from '../payment/payment';
import {PaymentModel} from '../payment/payment.model';
import {MenuItemData} from '../menu/menu-entity.model';
import {environment} from '../../environments/environment';
import {DeferredInjectable} from '../deferred-injection.service';
import {AppUserService} from '../identity/app-user.service';
import {ConfigurationService} from '@fedelta-pos/data-client';
import {Config} from '../configuration/config';

export enum LandingPage {
    ORDERING = "ordering",
    ENTRY = "entry",
    STORE_SELECTION = "store selection"
}

// noinspection JSUnusedGlobalSymbols
export class PromoShown {
    constructor(public promoName: string, public promoType: string, public itemName: string, public itemPrice: number, public itemPLU: string) {
    }
}

export class OrderPlacedEvent {
    public orderType: "pickup" | "table" | "delivery";
    public login: "guest" | "member";
    public amount: number;
    public transferredAmount: number;
    public walletPayment: boolean;

    // noinspection JSUnusedGlobalSymbols
    constructor(orderType: OrderType,
                guest: boolean,
                paymentAmount: number,
                public paymentType: PaymentType,
                public itemsOrdered: number,
                public walletPaymentAvailable: boolean,
                public quickPay: boolean,
                public tableNumber: string) {
        this.orderType = orderType === OrderType.ORDER_AHEAD ? "pickup" : orderType === OrderType.ORDER_AT_TABLE ? "table" : "delivery";
        this.login = guest ? "guest" : "member";
        this.amount = paymentType === PaymentType.GROUP_PAY ? 0 : paymentAmount;
        this.transferredAmount = paymentType === PaymentType.GROUP_PAY ? paymentAmount : 0;
        this.walletPayment = paymentType === PaymentType.APPLE_PAY || paymentType === PaymentType.GOOGLE_PAY;
    }
}

@Injectable({providedIn: "root"})
export class AnalyticsService implements DeferredInjectable {
    private configurationModel: ConfigurationModel = inject(ConfigurationModel);
    private configurationService: ConfigurationService = inject(ConfigurationService);
    private appUserService: AppUserService = inject(AppUserService);
    private sessionModel: SessionModel;
    private orderModel: OrderModel;
    private paymentModel: PaymentModel;
    private orderingContextModel: OrderingContextModel;

    private static readonly MEASUREMENT_PROTOCOL_URL: string = "https://www.google-analytics.com/mp/collect";

    private static readonly ANALYTICS_VERSION: number = 3;

    /**
     * App start should only be tracked once
     */
    private appStartTracked: boolean;

    private searchInitiatedTracked: boolean;
    private searchItemSelectedTracked: boolean;
    private pairingsActivatedTracked: boolean;
    private pairingsShownTracked: boolean;
    private pairingSelectedTracked: boolean;

    private scanType: string;

    private initialised: boolean;

    /**
     * Indicates if analytics is being captured for this session
     */
    private analyticsEnabled: boolean;

    private walletPaymentTracked: boolean;

    private promosShown: PromoShown[] = [];

    private googleKey: string;
    private apiSecret: string;

    /**
     * gtag.js accumulates foreground (visible + focused) time and attaches it to the next hit as
     * engagement_time_msec, then resets - this is what GA4 uses to compute engaged sessions/engagement
     * rate. Mirrored here since MP has no browser-side engagement tracking of its own.
     */
    private engagementAccumulatedMs: number = 0;
    private engagementSegmentStart: number = performance.now();

    constructor() {
        console.log("--- AnalyticsService");
        document.addEventListener("visibilitychange", () => {
            if (document.visibilityState === "visible") {
                this.engagementSegmentStart = performance.now();
            } else {
                this.engagementAccumulatedMs += performance.now() - this.engagementSegmentStart;
            }
        });
    }

    /**
     * Returns accumulated engaged foreground milliseconds since the last hit, then resets the counter.
     */
    private consumeEngagementTimeMs(): number {
        if (document.visibilityState === "visible") {
            const now: number = performance.now();
            this.engagementAccumulatedMs += now - this.engagementSegmentStart;
            this.engagementSegmentStart = now;
        }
        const engagementTimeMs: number = Math.round(this.engagementAccumulatedMs);
        this.engagementAccumulatedMs = 0;
        return engagementTimeMs;
    }

    public inject(): void {
        this.sessionModel = inject(SessionModel);
        this.orderModel = inject(OrderModel);
        this.paymentModel = inject(PaymentModel);
        this.orderingContextModel = inject(OrderingContextModel);
    }

    private async checkInitialised(): Promise<void> {
        if (!this.initialised) {
            await this.init();
            this.initialised = true;
        }
    }

    private async init(): Promise<void> {
        this.analyticsEnabled = !this.configurationModel.previewToken();
        if (!this.analyticsEnabled) {
            return;
        }

        // Initialise Google Analytics
        const isNonProd: boolean = this.configurationModel.devMode() || this.configurationModel.staging() || !this.configurationModel.app()?.appLiveMode;
        this.googleKey = isNonProd ? await this.configurationService.getString(Config.GOOGLE_ANALYTICS_NON_PROD_KEY)
            : this.configurationModel.isWaiterActor() ? await this.configurationService.getString(Config.GOOGLE_ANALYTICS_SERVICE_APP_PROD_KEY)
                : await this.configurationService.getString(Config.GOOGLE_ANALYTICS_PROD_KEY);
        this.apiSecret = isNonProd ? await this.configurationService.getString(Config.GOOGLE_ANALYTICS_NON_PROD_API_SECRET)
            : this.configurationModel.isWaiterActor() ? await this.configurationService.getString(Config.GOOGLE_ANALYTICS_SERVICE_APP_PROD_API_SECRET)
                : await this.configurationService.getString(Config.GOOGLE_ANALYTICS_PROD_API_SECRET);
    }

    async trackLandingPage(landingPage: LandingPage): Promise<void> {
        await this.checkInitialised();
        if (!this.appStartTracked) {
            this.appStartTracked = true;

            const browserRefresh: boolean = ((window.performance?.getEntriesByType?.("navigation") ?? []) as PerformanceNavigationTiming[])
                .some(e => e.type === 'reload');

            const paramConfig: ParamConfig = await this.configurationModel.getParamConfig();

            this.scanType = paramConfig.scanType || "none";

            const props: any = this.commonProps(paramConfig);
            props.cz_table_number = paramConfig.tableLookupCode;
            props.cz_table_only_mode = paramConfig.tableOnlyMode;
            props.cz_pickup_only_mode = paramConfig.pickupOnlyMode;
            props.cz_entry_scan_type = this.scanType;
            props.cz_browser_refresh = browserRefresh;
            props.cz_landing_page = landingPage;
            props.cz_integrated_loyalty = this.configurationModel.app().integratedLoyalty;
            this.sendEvent("cz_enter_app", props);
        }
    }

    async trackViewItem(menuItem: MenuItemData): Promise<void> {
        await this.checkInitialised();
        const paramConfig: ParamConfig = await this.configurationModel.getParamConfig();
        const props: any = this.commonProps(paramConfig);
        props.cz_item_price = menuItem.sellable.defaultPrice;
        props.cz_item_name = menuItem.sellable.name;
        props.cz_item_plu = menuItem.sellable.plu;
        props.cz_item_sellable_id = menuItem.sellable.sellableId;
        props.cz_promoted_feature_tag = menuItem.promotedFeatureTag;
        this.sendEvent("cz_item_viewed", props);
    }

    async trackItemAddedToOrder(orderItem: OrderItem, promotedFeatureTag?: string): Promise<void> {
        await this.checkInitialised();
        const paramConfig: ParamConfig = await this.configurationModel.getParamConfig();
        const props: any = this.commonProps(paramConfig);
        props.cz_item_from_deal = !!orderItem.dealIdSignal();
        props.cz_item_price = orderItem.sellable().price;
        props.cz_item_total = orderItem.localTotalSignal();
        props.cz_item_quantity = orderItem.quantitySignal();
        props.cz_item_name = orderItem.nameSignal();
        props.cz_item_plu = orderItem.sellable().plu;
        if (orderItem.sellable()) {
            props.cz_item_sellable_id = orderItem.sellable().sellableId;
        }
        this.sendEvent("cz_item_ordered", props);

        if (promotedFeatureTag) {
            props.cz_promoted_feature_tag = promotedFeatureTag;
            this.sendEvent("cz_promoted_item_ordered", props);
        }
    }

    async trackEditItem(orderItem: DisplayableOrderItem, fromNotification: boolean = false): Promise<void> {
        await this.checkInitialised();
        const paramConfig: ParamConfig = await this.configurationModel.getParamConfig();
        const props: any = this.commonProps(paramConfig);
        props.cz_item_name = orderItem.nameSignal();
        if (orderItem instanceof OrderItem) {
            props.cz_item_price = orderItem.sellable().price;
            props.cz_item_plu = orderItem.sellable().plu;
            props.cz_item_sellable_id = orderItem.sellable().sellableId;
            props.cz_item_total = orderItem.localTotalSignal();
            props.cz_item_quantity = orderItem.quantitySignal();
        }
        this.sendEvent("cz_item_edit", props);

        if (fromNotification) {
            this.sendEvent("cz_item_edit_from_notification", props);
        }
    }

    async trackItemEdited(orderItem: DisplayableOrderItem): Promise<void> {
        await this.checkInitialised();
        const paramConfig: ParamConfig = await this.configurationModel.getParamConfig();
        const props: any = this.commonProps(paramConfig);
        props.cz_item_name = orderItem.nameSignal();
        if (orderItem instanceof OrderItem) {
            props.cz_item_price = orderItem.sellable().price;
            props.cz_item_plu = orderItem.sellable().plu;
            props.cz_item_sellable_id = orderItem.sellable().sellableId;
            props.cz_item_total = orderItem.localTotalSignal();
            props.cz_item_quantity = orderItem.quantitySignal();
        }
        this.sendEvent("cz_item_edited", props);
    }

    async trackItemQtyChanged(orderItem: DisplayableOrderItem): Promise<void> {
        await this.checkInitialised();
        const paramConfig: ParamConfig = await this.configurationModel.getParamConfig();
        const props: any = this.commonProps(paramConfig);
        props.cz_item_name = orderItem.nameSignal();
        if (orderItem instanceof OrderItem) {
            props.cz_item_plu = orderItem.sellable().plu;
            props.cz_item_sellable_id = orderItem.sellable().sellableId;
            props.cz_item_quantity = orderItem.quantitySignal();
        }
        this.sendEvent("cz_item_qty_changed", props);
    }

    async trackItemRemoved(orderItem: DisplayableOrderItem): Promise<void> {
        await this.checkInitialised();
        const paramConfig: ParamConfig = await this.configurationModel.getParamConfig();
        const props: any = this.commonProps(paramConfig);
        props.cz_item_name = orderItem.nameSignal();
        if (orderItem instanceof OrderItem) {
            props.cz_item_price = orderItem.sellable().price;
            props.cz_item_plu = orderItem.sellable().plu;
            props.cz_item_sellable_id = orderItem.sellable().sellableId;
            props.cz_item_total = orderItem.localTotalSignal();
            props.cz_item_quantity = orderItem.quantitySignal();
        }
        this.sendEvent("cz_item_removed", props);
    }

    async track3DSStart(amount: number): Promise<void> {
        await this.checkInitialised();
        const paramConfig: ParamConfig = await this.configurationModel.getParamConfig();
        const props: any = this.commonProps(paramConfig);
        props.cz_order_amount = amount;
        this.sendEvent("cz_3ds_check_start", props);
    }

    async track3DSFailedOnClient(amount: number): Promise<void> {
        await this.checkInitialised();
        const paramConfig: ParamConfig = await this.configurationModel.getParamConfig();
        const props: any = this.commonProps(paramConfig);
        props.cz_order_amount = amount;
        props.cz_failure_location = "client";
        this.sendEvent("cz_3ds_check_failed", props);
    }

    async track3DSFailedOnServer(amount: number): Promise<void> {
        await this.checkInitialised();
        const paramConfig: ParamConfig = await this.configurationModel.getParamConfig();
        const props: any = this.commonProps(paramConfig);
        props.cz_order_amount = amount;
        props.cz_failure_location = "server";
        this.sendEvent("cz_3ds_check_failed", props);
    }

    async track3DSSucceeded(amount: number): Promise<void> {
        await this.checkInitialised();
        const paramConfig: ParamConfig = await this.configurationModel.getParamConfig();
        const props: any = this.commonProps(paramConfig);
        props.cz_order_amount = amount;
        this.sendEvent("cz_3ds_check_complete", props);
    }

    async trackNotReadyToPlaceOrder(): Promise<void> {
        await this.checkInitialised();
        const paramConfig: ParamConfig = await this.configurationModel.getParamConfig();
        const props: any = this.commonProps(paramConfig);
        if (this.orderModel.orderErrorStates().emptyOrderAttempted()) {
            props.cz_reject_order_reason = "emptyOrderAttempted";
        } else if (this.orderModel.orderErrorStates().orderWithInvalidTimeAttempted()) {
            props.cz_reject_order_reason = this.orderModel.initiatedOrder().unavailabilityReason === UnavailabilityReason.UNAVAILABLE_OFFLINE ? "storeOffline"
                : this.orderingContextModel.orderType() === OrderType.ORDER_AT_TABLE ? "storeUnavailableForTableOrders"
                    : !this.orderingContextModel.canShowAsapSelectionTime() && !this.orderingContextModel.expectedCompletion() ? "noTimeSelected"
                        : "invalidTime";
        } else if (this.orderModel.orderErrorStates().minMaxOrderAmountAttempted()) {
            props.cz_reject_order_reason = "minMaxOrderAmountAttempted";
        } else {
            props.cz_reject_order_reason = "unknown";
        }

        this.addStoreStatusProp(props);
        this.sendEvent("cz_order_not_ready", props);
    }

    async trackDelay(action: string): Promise<void> {
        await this.checkInitialised();
        const paramConfig: ParamConfig = await this.configurationModel.getParamConfig();
        const props: any = this.commonProps(paramConfig);
        props.cz_action = action;
        this.sendEvent("cz_unexpected_delay", props);
    }

    async trackAnomaly(description: string, error?: string): Promise<void> {
        await this.checkInitialised();
        const paramConfig: ParamConfig = await this.configurationModel.getParamConfig();
        const props: any = this.commonProps(paramConfig);
        props.cz_description = description;
        props.cz_error = error;
        this.sendEvent("cz_unexpected_anomaly", props);
    }

    async trackWalletPaymentRequestStarted(walletProvider: WalletProvider): Promise<void> {
        await this.checkInitialised();
        const paramConfig: ParamConfig = await this.configurationModel.getParamConfig();
        const props: any = this.commonProps(paramConfig);
        props.cz_payment_type = walletProvider;
        this.sendEvent("cz_wallet_payment_started", props);
    }

    async trackWalletPaymentRequestSucceeded(walletProvider: WalletProvider): Promise<void> {
        await this.checkInitialised();
        const paramConfig: ParamConfig = await this.configurationModel.getParamConfig();
        const props: any = this.commonProps(paramConfig);
        props.cz_payment_type = walletProvider;
        this.sendEvent("cz_wallet_payment_succeeded", props);
    }

    async trackWalletPaymentRequestCancelled(walletProvider: WalletProvider): Promise<void> {
        await this.checkInitialised();
        const paramConfig: ParamConfig = await this.configurationModel.getParamConfig();
        const props: any = this.commonProps(paramConfig);
        props.cz_payment_type = walletProvider;
        this.sendEvent("cz_wallet_payment_cancelled", props);
    }

    async trackPlaceOrderStarted(event: OrderPlacedEvent): Promise<void> {
        await this.checkInitialised();
        const paramConfig: ParamConfig = await this.configurationModel.getParamConfig();
        const props: any = this.commonProps(paramConfig);
        props.cz_payment_type = event.paymentType;
        props.cz_wallet_wayment = event.walletPayment;
        props.cz_wallet_payment_available = event.walletPaymentAvailable;
        props.cz_quick_pay = event.quickPay;
        props.paymentType = event.paymentType;
        props.cz_login = event.login;
        props.cz_items_ordered = event.itemsOrdered;
        props.cz_order_amount = event.amount;
        props.cz_entry_scan_type = this.scanType;
        props.cz_order_type = event.orderType;
        props.cz_table_number = event.tableNumber;
        props.cz_table_only_mode = paramConfig.tableOnlyMode;
        props.cz_pickup_only_mode = paramConfig.pickupOnlyMode;
        this.sendEvent("cz_place_order_start", props);
    }

    async trackPlaceOrderAbandoned(stage: string): Promise<void> {
        await this.checkInitialised();
        const paramConfig: ParamConfig = await this.configurationModel.getParamConfig();
        const props: any = this.commonProps(paramConfig);
        props.cz_order_abandoned_stage = stage;
        this.sendEvent("cz_place_order_abandoned", props);
    }

    async trackPlaceOrderFailed(reason: string, errorCode?: string, gatewayCode?: string): Promise<void> {
        await this.checkInitialised();
        const paramConfig: ParamConfig = await this.configurationModel.getParamConfig();
        const props: any = this.commonProps(paramConfig);
        props.cz_order_failed_reason = reason;
        props.cz_order_failed_error_code = errorCode;
        props.cz_order_failed_gateway_code = gatewayCode;
        this.sendEvent("cz_place_order_failed", props);
    }

    async trackOrderPlaced(event: OrderPlacedEvent): Promise<void> {
        await this.checkInitialised();
        // Revenue
        const paramConfig: ParamConfig = await this.configurationModel.getParamConfig();
        const props: any = this.commonProps(paramConfig);
        props.cz_payment_type = event.paymentType;
        props.cz_wallet_payment = event.walletPayment;
        props.cz_wallet_payment_available = event.walletPaymentAvailable;
        props.cz_quick_pay = event.quickPay;
        props.cz_payment_type = event.paymentType;
        props.cz_login = event.login;
        props.cz_items_ordered = event.itemsOrdered;
        props.cz_order_amount = event.amount;
        props.cz_entry_scan_type = this.scanType;
        props.cz_order_type = event.orderType;
        props.cz_table_number = event.tableNumber;
        props.cz_table_only_mode = paramConfig.tableOnlyMode;
        props.cz_pickup_only_mode = paramConfig.pickupOnlyMode;
        this.sendEvent("cz_place_order_complete", props);
    }

    async trackActivityTimeout(): Promise<void> {
        return this.sendSimpleEvent("cz_activity_timeout");
    }

    async trackOrderNotesEdited(): Promise<void> {
        return this.sendSimpleEvent("cz_order_notes_edited");
    }

    async trackOrderNotesRemoved(): Promise<void> {
        return this.sendSimpleEvent("cz_order_notes_removed");
    }

    async trackOrderPlacedWithOrderNotes(): Promise<void> {
        return this.sendSimpleEvent("cz_order_placed_with_order_notes");
    }

    async trackOrderItemNotesEdited(): Promise<void> {
        return this.sendSimpleEvent("cz_order_item_notes_edited");
    }

    async trackOrderItemNotesRemoved(): Promise<void> {
        return this.sendSimpleEvent("cz_order_item_notes_removed");
    }

    async trackOrderPlacedWithItemNotes(): Promise<void> {
        return this.sendSimpleEvent("cz_order_placed_with_item_notes");
    }

    /**
     * Track if Apple/Google Pay is available for a user
     */
    public async trackWalletPaymentStatus(walletPaymentAvailable: boolean): Promise<void> {
        await this.checkInitialised();
        if (this.initialised && !this.walletPaymentTracked) {
            this.walletPaymentTracked = true;
            const paramConfig: ParamConfig = await this.configurationModel.getParamConfig();
            const props: any = this.commonProps(paramConfig);
            if (walletPaymentAvailable) {
                this.sendEvent("cz_wallet_payment_available", props);
            } else {
                this.sendEvent("cz_wallet_payment_unavailable", props);
            }
        }
    }

    async trackWalletPaymentTransientActivationError(): Promise<void> {
        return this.sendSimpleEvent("cz_wallet_payment_transient_activation_error");
    }

    async trackWalletPaymentProviderNotReadyError(): Promise<void> {
        return this.sendSimpleEvent("cz_wallet_payment_provider_not_ready_error");
    }

    async trackSetStore(): Promise<void> {
        await this.checkInitialised();
        const paramConfig: ParamConfig = await this.configurationModel.getParamConfig();
        const props: any = this.commonProps(paramConfig);
        this.addStoreStatusProp(props);
        this.sendEvent("cz_set_store", props);
    }

    async trackReorderRoundShown(quickPayAvailable: boolean, itemTotal: number): Promise<void> {
        await this.checkInitialised();
        const paramConfig: ParamConfig = await this.configurationModel.getParamConfig();
        const props: any = this.commonProps(paramConfig);
        props.cz_quick_pay_available = quickPayAvailable;
        props.cz_item_total = itemTotal;
        this.sendEvent("cz_reorder_round_shown", props);
    }

    async trackReorderRoundAdded(itemTotal: number): Promise<void> {
        await this.checkInitialised();
        const paramConfig: ParamConfig = await this.configurationModel.getParamConfig();
        const props: any = this.commonProps(paramConfig);
        props.cz_item_total = itemTotal;
        this.sendEvent("cz_reorder_round_added", props);
    }

    async trackReorderRoundPaid(itemTotal: number): Promise<void> {
        await this.checkInitialised();
        const paramConfig: ParamConfig = await this.configurationModel.getParamConfig();
        const props: any = this.commonProps(paramConfig);
        props.cz_item_total = itemTotal;
        this.sendEvent("cz_reorder_round_paid", props);
    }

    async trackReorderRoundCancelled(itemTotal: number): Promise<void> {
        await this.checkInitialised();
        const paramConfig: ParamConfig = await this.configurationModel.getParamConfig();
        const props: any = this.commonProps(paramConfig);
        props.cz_item_total = itemTotal;
        this.sendEvent("cz_reorder_round_cancelled", props);
    }

    async trackSearchInitiated(): Promise<void> {
        if (!this.searchInitiatedTracked) {
            this.searchInitiatedTracked = true;
            return this.sendSimpleEvent("cz_search_initiated");
        }
    }

    async trackSearchItemSelected(): Promise<void> {
        if (!this.searchItemSelectedTracked) {
            this.searchItemSelectedTracked = true;
            return this.sendSimpleEvent("cz_search_item_selected");
        }
    }

    async trackQuickAddSelected(): Promise<void> {
        return this.sendSimpleEvent("cz_quick_add_selected");
    }

    async trackQuickAddSizeSelected(): Promise<void> {
        return this.sendSimpleEvent("cz_quick_add_size_selected");
    }

    async trackPairingsActivated(): Promise<void> {
        if (!this.pairingsActivatedTracked) {
            this.pairingsActivatedTracked = true;
            return this.sendSimpleEvent("cz_pairings_activated");
        }
    }

    async trackPairingsShown(): Promise<void> {
        if (!this.pairingsShownTracked) {
            this.pairingsShownTracked = true;
            return this.sendSimpleEvent("cz_pairings_shown");
        }
    }

    async trackPairingSelected(): Promise<void> {
        if (!this.pairingSelectedTracked) {
            this.pairingSelectedTracked = true;
            return this.sendSimpleEvent("cz_pairing_selected");
        }
    }

    async trackPromoShown(promoName: string, promoType: string, itemName: string, itemPrice: number, itemPLU: string): Promise<void> {
        await this.checkInitialised();

        // Only track card promos once per session (or once per session per promoted product)
        if (promoType === "card") {
            if (itemName) {
                if (this.promosShown.find((shown: PromoShown) => shown.promoName === promoName && shown.promoType === promoType && shown.itemName === itemName && shown.itemPLU === itemPLU)) {
                    return;
                }
            } else {
                if (this.promosShown.find((shown: PromoShown) => shown.promoName === promoName && shown.promoType === promoType)) {
                    return;
                }
            }
        }
        this.promosShown.push(new PromoShown(promoName, promoType, itemName, itemPrice, itemPLU));

        const paramConfig: ParamConfig = await this.configurationModel.getParamConfig();
        const props: any = this.commonProps(paramConfig);
        props.cz_promo_name = promoName;
        props.cz_promo_type = promoType;
        props.cz_item_name = itemName;
        props.cz_item_price = itemPrice;
        props.cz_item_plu = itemPLU;
        this.sendEvent("cz_promo_shown", props);
    }

    async trackPromoItemsShown(promoName: string, promoType: string): Promise<void> {
        await this.checkInitialised();
        const paramConfig: ParamConfig = await this.configurationModel.getParamConfig();
        const props: any = this.commonProps(paramConfig);
        props.cz_promo_name = promoName;
        props.cz_promo_type = promoType;
        this.sendEvent("cz_promo_items_shown", props);
    }

    async trackPromoItemOrdered(promoName: string, promoType: string, itemName: string, itemPrice: number, itemPLU: string): Promise<void> {
        await this.checkInitialised();
        const paramConfig: ParamConfig = await this.configurationModel.getParamConfig();
        const props: any = this.commonProps(paramConfig);
        props.cz_promo_name = promoName;
        props.cz_promo_type = promoType;
        props.cz_item_name = itemName;
        props.cz_item_price = itemPrice;
        props.cz_item_plu = itemPLU;
        this.sendEvent("cz_promo_ordered", props);
    }

    async trackPromoCancelled(promoName: string, promoType: string, itemName: string, itemPrice: number, itemPLU: string): Promise<void> {
        await this.checkInitialised();
        const paramConfig: ParamConfig = await this.configurationModel.getParamConfig();
        const props: any = this.commonProps(paramConfig);
        props.cz_promo_name = promoName;
        props.cz_promo_type = promoType;
        props.cz_item_name = itemName;
        props.cz_item_price = itemPrice;
        props.cz_item_plu = itemPLU;
        this.sendEvent("cz_promo_cancelled", props);
    }

    async trackExtraPromoShown(itemName: string, componentName: string, extraName: string, itemPLU: string, componentPLU: string): Promise<void> {
        await this.checkInitialised();
        const paramConfig: ParamConfig = await this.configurationModel.getParamConfig();
        const props: any = this.commonProps(paramConfig);
        props.cz_promo_type = "extra";
        props.cz_item_name = itemName;
        props.cz_component_name = componentName;
        props.cz_extra_name = extraName;
        props.cz_item_plu = itemPLU;
        props.cz_component_plu = componentPLU;
        this.sendEvent("cz_promo_shown", props);
    }

    async trackExtraPromoOrdered(itemName: string, componentName: string, extraName: string, itemPLU: string, componentPLU: string, priceChange: number): Promise<void> {
        await this.checkInitialised();
        const paramConfig: ParamConfig = await this.configurationModel.getParamConfig();
        const props: any = this.commonProps(paramConfig);
        props.cz_promo_type = "extra";
        props.cz_item_name = itemName;
        props.cz_component_name = componentName;
        props.cz_extra_name = extraName;
        props.cz_item_plu = itemPLU;
        props.cz_component_plu = componentPLU;
        props.cz_price_change = priceChange;
        this.sendEvent("cz_promo_ordered", props);
    }

    async trackExtraPromoCancelled(itemName: string, componentName: string, extraName: string, itemPLU: string, componentPLU: string): Promise<void> {
        await this.checkInitialised();
        const paramConfig: ParamConfig = await this.configurationModel.getParamConfig();
        const props: any = this.commonProps(paramConfig);
        props.cz_promo_type = "extra";
        props.cz_item_name = itemName;
        props.cz_component_name = componentName;
        props.cz_extra_name = extraName;
        props.cz_item_plu = itemPLU;
        props.cz_component_plu = componentPLU;
        this.sendEvent("cz_promo_cancelled", props);
    }

    async trackNavOption(optionName: string): Promise<void> {
        await this.checkInitialised();
        const paramConfig: ParamConfig = await this.configurationModel.getParamConfig();
        const props: any = this.commonProps(paramConfig);
        props.cz_option_name = optionName;
        this.sendEvent("cz_nav_option", props);
    }

    async trackFeatureButton(optionName: string): Promise<void> {
        await this.checkInitialised();
        const paramConfig: ParamConfig = await this.configurationModel.getParamConfig();
        const props: any = this.commonProps(paramConfig);
        props.cz_option_name = optionName;
        this.sendEvent("cz_feature_button", props);
    }

    /**
     * Properties tracked on all events
     */
    private commonProps(paramConfig: ParamConfig): any {
        const props: any = {};

        // GA4 reserved params gtag.js attaches to every hit automatically - MP doesn't have DOM/session
        // context, so they need to be supplied explicitly. session_id reuses the same id as cz_app_session_id.
        props.page_title = document.title;
        props.page_location = window.location.href;
        props.page_referrer = document.referrer || undefined;
        props.session_id = this.sessionModel.appSessionId();
        props.engagement_time_msec = this.consumeEngagementTimeMs();

        props.cz_analytics_version = AnalyticsService.ANALYTICS_VERSION;
        props.cz_app_session_id = this.sessionModel.appSessionId();
        props.cz_scan_id = this.sessionModel.scanId();
        props.cz_beacon_d = this.sessionModel.beaconId();
        props.cz_app_version = environment.version;
        props.cz_client_id = this.configurationModel.clientId();
        props.cz_app_name = this.configurationModel.appName();
        props.cz_client_app = this.configurationModel.clientId() + "/" + this.configurationModel.appName();
        props.cz_payment_provider = this.paymentModel.paymentProvider();
        if (this.orderingContextModel.currentStore()) {
            props.cz_store_name = this.orderingContextModel.currentStore().collectiveStoreName;
            props.cz_store_id = this.orderingContextModel.currentStore().storeId;
            props.cz_venue_id = this.orderingContextModel.currentStore().venueId;
        } else {
            props.cz_store_id = paramConfig.exclusiveStore;
        }

        const weekday = [];
        weekday[0] = "Sun";
        weekday[1] = "Mon";
        weekday[2] = "Tue";
        weekday[3] = "Wed";
        weekday[4] = "Thu";
        weekday[5] = "Fri";
        weekday[6] = "Sat";
        props.cz_day_of_week = weekday[new Date().getDay()];

        const month = [];
        month[0] = "January";
        month[1] = "February";
        month[2] = "March";
        month[3] = "April";
        month[4] = "May";
        month[5] = "June";
        month[6] = "July";
        month[7] = "August";
        month[8] = "September";
        month[9] = "October";
        month[10] = "November";
        month[11] = "December";
        props.cz_month = month[new Date().getMonth()];

        return props;
    }

    private addStoreStatusProp(props: any): any {
        if (!this.orderingContextModel.currentStore()) {
            props.cz_store_status = "noStore";
        } else if (this.orderingContextModel.currentStore().closed) {
            props.cz_store_status = "closed";
        } else if (this.orderingContextModel.currentStore().offline) {
            props.cz_store_status = "offline";
        } else {
            props.cz_store_status = "open";
        }
        return props;
    }

    private async sendSimpleEvent(eventName: string): Promise<void> {
        await this.checkInitialised();
        this.sendEvent(eventName, this.commonProps(await this.configurationModel.getParamConfig()));
    }

    /**
     * Sends the event directly to GA4's Measurement Protocol (server-side collection endpoint) rather than
     * via gtag.js - there is no browser library involved, so this has no cookie/storage/consent dependency at all.
     * Fire-and-forget: callers don't await this, so failures are caught and logged here rather than thrown.
     */
    private sendEvent(eventName: string, params: any): void {
        if (!this.analyticsEnabled) {
            return;
        }
        const url: string = `${AnalyticsService.MEASUREMENT_PROTOCOL_URL}?measurement_id=${encodeURIComponent(this.googleKey)}&api_secret=${encodeURIComponent(this.apiSecret)}`;
        fetch(url, {
            method: "POST",
            body: JSON.stringify({
                client_id: this.appUserService.getAppUserId(),
                // GA4 ignores user_agent entirely if device is present, so device must carry browser/OS itself rather than relying on GA's own user_agent parsing.
                device: this.buildDeviceInfo(),
                // MP doesn't derive geo from the connecting request's IP automatically.
                ip_override: this.configurationModel.app().clientIpAddress,
                events: [{name: eventName, params}]
            })
        }).catch(error => console.error("Failed to send analytics event", eventName, error));
    }

    /**
     * GA4 Measurement Protocol has no built-in User-Agent parsing, so browser/OS are derived here.
     * Prefers the Client Hints API (Chromium) for browser name/version, falling back to regex parsing
     * of navigator.userAgent for OS name/version and for non-Chromium browsers.
     */
    private buildDeviceInfo(): any {
        const ua: string = navigator.userAgent;
        const uaData: any = (navigator as any).userAgentData;
        const brandEntry: any = uaData?.brands?.find((b: any) => !/Not.*Brand/i.test(b.brand));

        return {
            category: uaData ? (uaData.mobile ? "mobile" : "desktop") : this.parseDeviceCategory(ua),
            language: navigator.language,
            screen_resolution: `${screen.width}x${screen.height}`,
            operating_system: this.parseOperatingSystem(ua),
            operating_system_version: this.parseOperatingSystemVersion(ua),
            browser: brandEntry?.brand ?? this.parseBrowserName(ua),
            browser_version: brandEntry?.version ?? this.parseBrowserVersion(ua)
        };
    }

    private parseDeviceCategory(ua: string): string {
        if (/iPad|Tablet|(Android(?!.*Mobile))/i.test(ua)) {
            return "tablet";
        }
        return /Mobi|iPhone|Android/i.test(ua) ? "mobile" : "desktop";
    }

    private parseOperatingSystem(ua: string): string {
        if (/Windows/i.test(ua)) {
            return "Windows";
        } else if (/iPhone|iPad|iPod/i.test(ua)) {
            return "iOS";
        } else if (/Mac OS X/i.test(ua)) {
            return "macOS";
        } else if (/Android/i.test(ua)) {
            return "Android";
        } else if (/CrOS/i.test(ua)) {
            return "Chrome OS";
        } else if (/Linux/i.test(ua)) {
            return "Linux";
        }
        return undefined;
    }

    private parseOperatingSystemVersion(ua: string): string {
        const windowsMatch: RegExpMatchArray = ua.match(/Windows NT ([\d.]+)/);
        const iOSMatch: RegExpMatchArray = ua.match(/OS ([\d_]+) like Mac OS X/);
        const macMatch: RegExpMatchArray = ua.match(/Mac OS X ([\d_]+)/);
        const androidMatch: RegExpMatchArray = ua.match(/Android ([\d.]+)/);
        return windowsMatch?.[1] ?? iOSMatch?.[1]?.replace(/_/g, ".") ?? macMatch?.[1]?.replace(/_/g, ".") ?? androidMatch?.[1];
    }

    // Order matters: Chrome/Edge/Opera/Samsung Internet UAs all also contain "Chrome" and/or "Safari" tokens,
    // so more specific browsers must be checked before the more generic ones they're built on.
    private parseBrowserName(ua: string): string {
        if (/Edg\//.test(ua)) {
            return "Edge";
        } else if (/OPR\//.test(ua)) {
            return "Opera";
        } else if (/SamsungBrowser/.test(ua)) {
            return "Samsung Internet";
        } else if (/Firefox/.test(ua)) {
            return "Firefox";
        } else if (/Chrome|CriOS/.test(ua)) {
            return "Chrome";
        } else if (/Safari/.test(ua)) {
            return "Safari";
        }
        return undefined;
    }

    private parseBrowserVersion(ua: string): string {
        const edgeMatch: RegExpMatchArray = ua.match(/Edg\/([\d.]+)/);
        const operaMatch: RegExpMatchArray = ua.match(/OPR\/([\d.]+)/);
        const samsungMatch: RegExpMatchArray = ua.match(/SamsungBrowser\/([\d.]+)/);
        const firefoxMatch: RegExpMatchArray = ua.match(/Firefox\/([\d.]+)/);
        const chromeMatch: RegExpMatchArray = ua.match(/(?:Chrome|CriOS)\/([\d.]+)/);
        const safariMatch: RegExpMatchArray = ua.match(/Version\/([\d.]+)/);
        return edgeMatch?.[1] ?? operaMatch?.[1] ?? samsungMatch?.[1] ?? firefoxMatch?.[1] ?? chromeMatch?.[1] ?? safariMatch?.[1];
    }
}
